import type { MouseEvent } from 'react';

export default function HeroSection() {
  const heroImages = ['/images/hero/1.png', '/images/hero/2.png', '/images/hero/3.png', '/images/hero/4.png'];
  const handleAnchorClick = (targetId: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    const isHome = window.location.pathname === '/';
    const target = document.querySelector(targetId);

    if (isHome && target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.pushState(null, '', targetId);
      return;
    }

    window.location.href = `/${targetId}`;
  };

  return (
    <section
      id="hero"
      className="relative overflow-hidden text-[#0F172A]"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 overflow-hidden">
          {heroImages.map((image, index) => (
            <div
              key={image}
              className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-0 animate-[heroFade_24s_infinite]"
              style={{
                backgroundImage: `url(${image})`,
                animationDelay: `${index * 6}s`,
              }}
            />
          ))}
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.92)_0%,rgba(234,244,255,0.78)_38%,rgba(255,255,255,0.96)_72%,#FFFFFF_100%)]" />
        <div className="absolute left-[-8rem] top-[-10rem] h-[22rem] w-[22rem] rounded-full bg-[#D9EEFF]/70 blur-3xl" />
        <div className="absolute right-[-8rem] top-1/4 h-[24rem] w-[24rem] rounded-full bg-[#B9DEFF]/40 blur-3xl" />
        <div className="absolute bottom-[-10rem] left-1/3 h-[22rem] w-[22rem] rounded-full bg-white blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-24 pb-14 sm:pt-28 sm:pb-16 md:pt-36 md:pb-24">
        <style>{`@keyframes heroFade { 0%, 20% { opacity: 0; } 25%, 45% { opacity: 1; } 50%, 100% { opacity: 0; } }`}</style>
        <div className="grid items-center gap-10 lg:grid-cols-1 lg:gap-12">
          <div className="relative order-1 max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B9DEFF] bg-white/90 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#027FDB] shadow-[0_10px_30px_rgba(2,127,219,0.08)] backdrop-blur-md sm:text-xs">
              <span className="h-2 w-2 rounded-full bg-[#027FDB]" />
              Berlin · §45b SGB XI · Haushaltshilfe
            </div>

            <h1 className="mt-5 max-w-2xl text-[2.45rem] font-semibold leading-[0.98] tracking-[-0.035em] text-[#0F172A] sm:text-[3.35rem] lg:text-[4.6rem]">
              Alltagsstern Haushaltshilfe in Berlin,
              <span className="mt-2 block text-[#027FDB]">persönlich und verlässlich</span>
            </h1>

            <p className="mt-5 max-w-[34rem] text-[0.98rem] leading-7 text-[#475569] sm:text-[1.06rem] sm:leading-8">
              Unterstützung für Personen mit Pflegegrad und Alltagshilfe nach §45b SGB XI. Alltagsstern UG begleitet im Haushalt, bei Erledigungen und im Alltag in Berlin.
            </p>

            <div className="mt-7 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
              <a
                href="#kontakt"
                onClick={handleAnchorClick('#kontakt')}
                className="inline-flex min-h-[3.8rem] w-full items-center justify-center gap-2.5 rounded-2xl border border-[#027FDB] bg-[#027FDB] px-7 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_18px_44px_rgba(2,127,219,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#026BB8] hover:bg-[#026BB8] sm:w-auto sm:min-w-[23rem]"
              >
                <i className="ri-mail-send-line text-xl sm:text-2xl" />
                Unverbindliche Erstberatung anfragen
              </a>

              <a
                href="tel:+4915229990328"
                className="inline-flex min-h-[3.8rem] w-full items-center justify-center gap-2.5 rounded-2xl border border-[#B9DEFF] bg-white/95 px-7 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-[#027FDB] shadow-[0_12px_30px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#027FDB] hover:bg-[#EAF4FF] hover:text-[#026BB8] sm:w-auto sm:min-w-[16rem]"
              >
                <i className="ri-phone-line text-xl sm:text-2xl" />
                Direkt anrufen
              </a>
            </div>

            <div className="mt-4 inline-flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-[#D9EEFF] bg-white/88 px-4 py-2.5 text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-[#0F172A] shadow-[0_10px_24px_rgba(15,23,42,0.05)] backdrop-blur-sm sm:text-[0.74rem]">
              <span className="inline-flex items-center gap-1.5 text-[#027FDB]">
                <i className="ri-time-line text-sm" aria-hidden="true" />
                Rückmeldung i. d. R. in 24h
              </span>
              <span className="hidden sm:block h-1 w-1 rounded-full bg-[#B9DEFF]" />
              <span className="inline-flex items-center gap-1.5 text-[#0F172A]">
                <i className="ri-user-heart-line text-sm text-[#027FDB]" aria-hidden="true" />
                Persönliche Erstberatung
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-2.5 max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#D9EEFF] bg-white/92 px-3.5 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-[#0F172A] shadow-[0_10px_24px_rgba(15,23,42,0.05)] backdrop-blur-sm sm:text-xs">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EAF4FF] text-base text-[#027FDB]">
                  <i className="ri-map-pin-line" />
                </span>
                Berlinweit
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-[#D9EEFF] bg-white/92 px-3.5 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-[#0F172A] shadow-[0_10px_24px_rgba(15,23,42,0.05)] backdrop-blur-sm sm:text-xs">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EAF4FF] text-base text-[#027FDB]">
                  <i className="ri-shield-check-line" />
                </span>
                §45b SGB XI
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-[#D9EEFF] bg-white/92 px-3.5 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-[#0F172A] shadow-[0_10px_24px_rgba(15,23,42,0.05)] backdrop-blur-sm sm:text-xs">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EAF4FF] text-base text-[#027FDB]">
                  <i className="ri-user-heart-line" />
                </span>
                Persönliche Erstberatung
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-[#D9EEFF] bg-white/92 px-3.5 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-[#0F172A] shadow-[0_10px_24px_rgba(15,23,42,0.05)] backdrop-blur-sm sm:text-xs">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EAF4FF] text-base text-[#027FDB]">
                  <i className="ri-time-line" />
                </span>
                Antwort i. d. R. in 24h
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
